import { Mail } from 'lucide-react'
import type { Donation } from '@/features/donations/types/donation'
import { Badge } from '@/components/custom/badge'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { DonationCard } from '@/features/donations/components/DonationCard'
import SendBulkReceiptsButton from '@/features/receipts/components/SendBulkReceiptsButton'
import { useIsMobile } from '@/hooks'

type DonationsWithoutSentReceiptsProps = {
  donations: Array<Donation>
  donorNameResolver: (donorId: string | null) => string
}

export function DonationsWithoutSentReceipts({
  donations,
  donorNameResolver,
}: DonationsWithoutSentReceiptsProps) {
  const isMobile = useIsMobile()

  if (!isMobile) {
    return (
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Mail className="h-5 w-5 text-yellow-600 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">
                Donations Without Sent Receipts
              </h2>
            </div>
            <div>
              <Badge color="yellow">{donations.length} unsent</Badge>
              <SendBulkReceiptsButton />
            </div>
          </div>
        </div>
        <div className="p-6">
          {donations.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                No verified donations need receipts
              </p>
            </div>
          ) : (
            <ScrollArea className="w-full overflow-x-auto">
              <div className="inline-flex gap-4 w-[75vw]">
                {donations.slice(0, 6).map((donation) => (
                  <DonationCard
                    key={donation._id}
                    donation={donation}
                    type="unsent"
                    donorName={donorNameResolver(donation.donorId)}
                  />
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}
        </div>
      </div>
    )
  }

  return donations.length === 0 ? (
    <div className="text-center py-8">
      <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
      <p className="text-gray-500">No verified donations need receipts</p>
    </div>
  ) : (
    <ScrollArea className="flex-1">
      <div className="mb-4">
        <SendBulkReceiptsButton />
      </div>
      <div className="space-y-3">
        {donations.map((donation) => (
          <DonationCard
            key={donation._id}
            donation={donation}
            type="unsent"
            donorName={donorNameResolver(donation.donorId)}
          />
        ))}
      </div>
    </ScrollArea>
  )
}
